/**
 * TourOverlay (#124) — the ONE globally-mounted piece that turns the tour
 * controller's state into a spotlight tour over the REAL app pages.
 *
 * Mounted once in the authed shell (routes/_layout.tsx), inside the router +
 * providers. When a tour is active it:
 *   1. Navigates to the current step's `route` (react-router) if we're not
 *      already there — so the real page is on screen.
 *   2. Resolves the step's `data-tour` anchor on that page (useTourAnchor:
 *      query → MutationObserver + poll → timeout → graceful skip).
 *   3. Paints the Spotlight hole + ring over the anchor and a StepCard beside
 *      it with the guiding copy + Back / Next / Skip.
 *   4. On the FINAL step's button, shows a completion celebration (StepCard
 *      celebrate mode) before marking the tour done + closing.
 *   5. Shows a small persistent "Tutorial" active indicator (bottom-left) so
 *      the user always knows a tour is running and can exit.
 *
 * SAFETY: this component only reads controller state, navigates, and paints. It
 * NEVER clicks the real UI, submits forms, or calls the API. Real actions stay
 * the user's to take. Navigation is the one side effect, and it's idempotent
 * (guarded on pathname) so it can't loop or fire a write.
 */

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GraduationCap, X } from "lucide-react";
import { useOptionalToast } from "@/lib/ui/toast";
import { useTour } from "./tour-controller";
import { useTourAnchor } from "./useTourAnchor";
import { Spotlight } from "./Spotlight";
import { StepCard } from "./StepCard";

export function TourOverlay() {
  const {
    tutorial,
    step,
    stepIndex,
    isActive,
    isLast,
    resumed,
    next,
    back,
    restart,
    finish,
    stop,
  } = useTour();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useOptionalToast();

  // Local "showing the wrap-up" flag. Pressing the final step's button flips
  // this on (the celebration replaces the step body); Done → finish().
  const [celebrating, setCelebrating] = useState(false);

  // Reset the celebration whenever the active step or tour changes, so moving
  // off the last step (Back) or starting another tour never shows a stale
  // wrap-up. Keyed on identity + index, not the boolean, to avoid a loop.
  useEffect(() => {
    setCelebrating(false);
  }, [tutorial, stepIndex]);

  // Forget which steps were auto-skipped whenever the running tour changes
  // (start / restart / switch). A fresh run re-evaluates every step's anchor
  // from scratch — a target absent last time (panel closed) may be present now.
  const skippedKeysRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    skippedKeysRef.current = new Set();
  }, [tutorial]);

  // Route-aware stepping: bring the real page on screen for the active step.
  // Guarded on pathname so we navigate only when actually off-route (no loop,
  // no repeated history entries, no chance of re-triggering a page side effect).
  const targetRoute = step?.route ?? null;
  useEffect(() => {
    if (!targetRoute) return;
    if (location.pathname !== targetRoute) {
      navigate(targetRoute);
    }
  }, [targetRoute, location.pathname, navigate]);

  // Resolve + track the current anchor's rect. Disabled (null) while idle or
  // until we've navigated onto the step's route — querying before the page
  // swaps could match a stale element on the previous page.
  const onRoute = !!targetRoute && location.pathname === targetRoute;
  const anchorActive = isActive && onRoute;
  // Optional anchors live behind a user action (opening a dialog/sheet), so give
  // the user a generous window before we stop hunting. A required anchor sits on
  // the page at rest — if it isn't there shortly after the route settles, it
  // isn't coming (feature off, empty-state, collapsed/responsive layout), so use
  // a shorter window before auto-skipping.
  const isOptional = step?.optionalAnchor === true;
  const { rect, resolved, timedOut } = useTourAnchor(
    anchorActive ? (step?.anchor ?? null) : null,
    isOptional ? 8000 : 3500,
  );

  // Graceful missing-target handling (#124). A step's anchor can be genuinely
  // absent — the feature's off for this org, a panel is collapsed, a responsive
  // layout dropped the node, an empty-state replaced the real one. We must NEVER
  // freeze the tour on a centered card spotlighting nothing.
  //
  // Two cases, by step kind:
  //   - REQUIRED step (no `optionalAnchor`): if the wait elapses without the
  //     element ever appearing, auto-skip — advance to the next step, or finish
  //     if it was the last — with a soft, non-alarming note. The workflow reads
  //     on; nothing throws or stalls.
  //   - OPTIONAL step (`optionalAnchor`, e.g. a dialog the user may not have
  //     opened): we DON'T skip. The StepCard centers as pure guidance ("Click X
  //     to …") and the user advances with Next when ready. No toast — absence is
  //     expected here, not a failure.
  //
  // Guarded by the already-skipped step-key set (reset per tour, above) so a
  // given step auto-skips at most once — it can never loop, even if `next()`
  // were ever a no-op. (Going Back into a step we already skipped leaves it on
  // the centered guidance card; the user advances with Next.)
  useEffect(() => {
    if (!isActive || !tutorial || !step) return;
    if (celebrating) return; // wrap-up isn't anchored to anything
    if (!anchorActive) return; // still navigating onto the route
    if (!timedOut || resolved || isOptional) return;

    const stepKey = `${tutorial.id}#${stepIndex}`;
    if (skippedKeysRef.current.has(stepKey)) return;
    skippedKeysRef.current.add(stepKey);

    if (isLast) {
      toast.push(
        "info",
        `Skipped "${step.title}" — that part of the page isn't available here. Tour finished.`,
        { durationMs: 4500 },
      );
      finish();
    } else {
      toast.push(
        "info",
        `Skipped "${step.title}" — that part of the page isn't available right now.`,
        { durationMs: 4000 },
      );
      next();
    }
  }, [
    isActive,
    tutorial,
    step,
    stepIndex,
    celebrating,
    anchorActive,
    timedOut,
    resolved,
    isOptional,
    isLast,
    toast,
    finish,
    next,
  ]);

  // A required anchor that hasn't resolved yet (and hasn't timed out) is still
  // being hunted — show the StepCard a soft "looking for this" note rather than
  // a silent empty spotlight. Optional anchors stay quiet (absence is expected).
  const searching = anchorActive && !resolved && !timedOut && !isOptional && !rect;

  // Esc exits the tour (matches the dim-click + X affordances). During the
  // celebration the user has already reached the end, so Esc records the
  // completion (finish) rather than a bare exit (stop).
  useEffect(() => {
    if (!isActive) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (celebrating) finish();
      else stop();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isActive, celebrating, finish, stop]);

  if (!isActive || !tutorial || !step) return null;

  // The final step's primary button opens the celebration instead of ending
  // abruptly; every earlier step advances normally.
  const handleNext = () => {
    if (isLast) setCelebrating(true);
    else next();
  };

  const handleRestart = () => {
    setCelebrating(false);
    restart();
  };

  return (
    <>
      {/* Spotlight dim + hole. The dim is purely visual (pointer-events-none):
          every real control stays clickable so the user can follow the workflow
          on the genuine UI. During the celebration we drop the hole for a clean
          full-screen dim behind the wrap-up card. */}
      <Spotlight rect={celebrating ? null : rect} />

      <StepCard
        title={step.title}
        body={step.body}
        placement={step.placement}
        stepIndex={stepIndex}
        stepCount={tutorial.steps.length}
        rect={rect}
        onBack={back}
        onNext={handleNext}
        onSkip={stop}
        isLast={isLast}
        celebrating={celebrating}
        tutorialTitle={tutorial.title}
        onFinish={finish}
        onRestart={handleRestart}
        resumed={resumed}
        searching={searching}
      />

      <TourIndicator title={tutorial.title} onExit={celebrating ? finish : stop} />
    </>
  );
}

/**
 * Small persistent badge (bottom-left, above the dim) telling the user a guided
 * tutorial is active and offering a one-click exit. Token-styled, z-50 so it
 * sits above the Spotlight dim alongside the StepCard.
 */
function TourIndicator({ title, onExit }: { title: string; onExit: () => void }) {
  return (
    <div className="fixed bottom-4 left-4 z-50 flex items-center gap-2 rounded-full border border-border bg-surface-elevated px-3 py-1.5 text-xs font-medium text-on-surface shadow-lg animate-fade-in">
      <GraduationCap className="size-4 text-brand" />
      <span className="text-on-surface-muted">Tutorial:</span>
      <span className="max-w-[12rem] truncate">{title}</span>
      <button
        type="button"
        onClick={onExit}
        aria-label="Exit tutorial"
        className="ml-1 rounded-full p-0.5 text-on-surface-muted transition-colors hover:bg-surface-muted hover:text-on-surface"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
